<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white">Sièges</v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <table-header-buttons
            :attr="toolTipText"
            @add="add"
            @remove="remove"
            @reload="reloadData"
            @export="exportTable"
            @search="updateSearchPanel"
            :has-selected-lines="selectedAccountIds.length > 0"
          ></table-header-buttons>
        </div>
      </v-toolbar>
      <Table
        :loading="loading"
        :search-field="true"
        :columns="columns"
        :items="accounts"
        :show-select="true"
        storageKey="accountTablePaginationState"
        v-model:selected="selectedAccountIds"
        @edit="edit"
        @row-clicked="rowClicked"
        @selectionChanged="selectionChanged"
      ></Table>
    </v-card>
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <search-panel
      :right-drawer="rightDrawer"
      @cancel-search="cancelSearch"
      @search-data="searchAccounts"
    >
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field
            name="name"
            label="Nom"
            light
            v-model="searchFilter.contain.name"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field
            name="signataire"
            label="Signataire"
            light
            v-model="searchFilter.contain.signataire"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field
            name="telephone"
            label="Telephone"
            light
            v-model="searchFilter.contain.telephone"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field
            name="email"
            label="Courriel"
            light
            v-model="searchFilter.contain.email"
          ></v-text-field>
        </v-col>
      </v-row>
    </search-panel>

    <ConfirmDialog
      v-model="dialog"
      :dialog-title="dialogTitle"
      :dialog-text="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
    ></ConfirmDialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import {
  buildSearchFilters,
  buildJsonServerQuery,
  clearSearchFilters,
} from "@/utils/app-util";
import { useAppStore } from "@/store/app";
import { createAndSaveDocument } from "@/utils/docx-util";
import type { Account, TableColumn } from "@/types";
import { getAccountExport } from "@/types";
import {
  deleteAccount,
  getAccountById,
  getAllAccounts,
  searchForAccounts,
} from "@/utils/account-api";

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false);
const dialogTitle = ref("Suppression");
const dialogText = ref("Voulez-vous supprimer les comptes siège sélectionnés?");
const showSearchPanel = ref(false);
const loading = ref(false);
const accounts = ref<Account[]>([]);
const selectedAccountIds = ref<string[]>([]);
const query = ref("");
const searchFilter = ref({
  contain: {
    name: "",
    signataire: "",
    telephone: "",
    email: "",
  },
});

const columns: TableColumn[] = [
  { title: "Nom", key: "name", align: "start" },
  { title: "Signataire", key: "signataire" },
  { title: "Téléphone", key: "telephone" },
  { title: "Courriel", key: "email" },
  { title: "Famille", key: "famille" },
  { title: "Nb Laveries", key: "nbLaveries" },
  { title: "", key: "actions", sortable: false },
];

const toolTipText = "un compte siège";

const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: (value: boolean) => {
    showSearchPanel.value = value;
  },
});

const selectionChanged = (selected: string[]) => {
  selectedAccountIds.value = [...selected];
};

const rowClicked = (item: Account) => {
  router.push(`/accounts/${item.id}`);
};

const edit = (item: Account) => {
  router.push(`/accounts/${item.id}`);
};

const add = () => {
  router.push("/NewAccount");
};

const remove = () => {
  dialog.value = true;
};

const onConfirm = async () => {
  try {
    loading.value = true;
    dialog.value = false;
    await deleteAccount(selectedAccountIds.value);
    await fetchAccounts();
    selectedAccountIds.value = [];
    appStore.sendSuccessNotice("Account has been deleted.");
  } finally {
    loading.value = false;
  }
};

const onCancel = () => {
  dialog.value = false;
};

const searchAccounts = async () => {
  showSearchPanel.value = !showSearchPanel.value;
  buildSearchFilters(searchFilter.value);
  query.value = buildJsonServerQuery(searchFilter.value);
  accounts.value = await searchForAccounts(query.value);
};

const _clearSearchFilters = () => {
  showSearchPanel.value = !showSearchPanel.value;
  clearSearchFilters(searchFilter.value);
  fetchAccounts();
};

const reloadData = () => {
  query.value = "";
  fetchAccounts();
};

const exportTable = async () => {
  if (loading.value) {
    return;
  }

  const accountIds = [...selectedAccountIds.value];
  if (!accountIds.length) {
    return;
  }

  try {
    loading.value = true;
    const selectedAccounts: Account[] = await Promise.all(
      accountIds.map((id) => getAccountById(id))
    );

    createAndSaveDocument(
      accountExportFileName(),
      accountExportTemplatePath(),
      getAccountExport(selectedAccounts)
    );
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

const accountExportFileName = (): string => {
  return "export_sieges";
};

const accountExportTemplatePath = (): string => {
  return "../templates/ExportSiegesTemplate.docx";
};

const updateSearchPanel = () => {
  rightDrawer.value = !rightDrawer.value;
};

const cancelSearch = () => {
  showSearchPanel.value = false;
};

const fetchAccounts = async () => {
  try {
    loading.value = true;
    accounts.value = await getAllAccounts();
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchAccounts();
});
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
